import axios from "axios";

// Date Time Format
export const formatTimeDate = (dateString) => {
  const options = {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  return new Intl.DateTimeFormat("en-US", options).format(new Date(dateString));
};

// Date format
export const formatDate = (dateString, fallback = "Invalid Date") => {
  const options = {
    month: "short",
    day: "2-digit",
    year: "numeric",
  };

  return new Intl.DateTimeFormat("en-US", options).format(new Date(dateString));
};

// Get all questions
export const fetchQuestions = async (setLoading, setError, setQuestions) => {
  try {
    setLoading(true);
    setError(null);

    const response = await axios.get(`http://localhost:9200/api/question/all`);

    setQuestions(response.data);
  } catch (err) {
    setError(err.message);
    console.error("Error fetching questions:", err);
  } finally {
    setLoading(false);
  }
};

// Get selected question
export const getSingleQuestion = async (
  id,
  setLoading,
  setQuestionData = null
) => {
  try {
    setLoading(true);
    const response = await axios.get(
      `http://localhost:9200/api/question/${id}`
    );
    const questionData = response?.data || null;

    // Ensure Answers is always an array
    if (questionData?.answers) {
      questionData.answers.forEach((answer) => {
        if (!Array.isArray(answer.Answers)) {
          answer.Answers = [answer.Answers];
        }
      });
    }

    // If setQuestionData is provided, update state
    if (setQuestionData) {
      setQuestionData(questionData);
    }

    return questionData; // Always return the question data
  } catch (error) {
    console.error("Error fetching question:", error);
    if (setQuestionData) {
      setQuestionData(null); // Set state to null if provided
    }
    return null; // Return null in case of an error
  } finally {
    setLoading(false);
  }
};

// Create
export const createQuestion = async (
  values,
  setLoading,
  setError,
  setQuestions
) => {
  let answersData;
  if (values.questiontype === "MCQ") {
    // Ensure `Options` and `Answers` are present and valid
    if (
      values.answers[0]?.Options?.length < 3 ||
      !values.answers[0]?.Answers?.length
    ) {
      alert("MCQ requires at least 3 options and 1 answer.");
      return;
    }

    answersData = [
      {
        Options: values.answers[0].Options, // Dynamic options for MCQs
        Answers: values.answers[0].Answers.filter((a) => a.trim() !== ""), // Selected answers
      },
    ];
  } else {
    // Ensure `Answers` is valid for non-MCQ types
    if (values.answers[0].Answers[0] === "") {
      alert("Answer is required ");
      return;
    }

    answersData = [
      {
        Answers: values.answers[0].Answers,
      },
    ];
  }

  const createdData = {
    questionTitle: values.question,
    difficulty: values.difficulty,
    level: values.level,
    topic: values.topic,
    questiontype: values.questiontype,
    answers: answersData,
  };

  try {
    const response = await axios.post(
      `http://localhost:9200/api/question/create`,
      createdData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    fetchQuestions(setLoading, setError, setQuestions);
    alert("Question created successful!");
  } catch (error) {
    console.error("Error creating question:", error);
    alert("Failed to create question. Please try again.");
  }
};

// Update
export const handleUpdate = async (
  id,
  values,
  setLoading,
  setError,
  setQuestions
) => {
  let answersData;
  if (values.questiontype === "MCQ") {
    // For MCQs, we include both Options and Answers
    answersData = [
      {
        Options: values.answers[0].Options || [], // Dynamic options for MCQs
        Answers: (values.answers[0].Answers || []).filter(
          (a) => a.trim() !== ""
        ), // Selected answers
      },
    ];
  } else {
    // For other types like Descriptive, we just include Answers
    answersData = [
      {
        Answers: [values.answers[0].Answers[0]], // Use single answer for non-MCQ types
      },
    ];
  }
  const updateData = {
    questionTitle: values.question,
    difficulty: values.difficulty,
    level: values.level,
    topic: values.topic,
    questiontype: values.questiontype,
    answers: answersData,
  };

  try {
    const response = await axios.put(
      `http://localhost:9200/api/question/update/${id}`,
      updateData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    fetchQuestions(setLoading, setError, setQuestions);

    alert("Question updated successfully!");
  } catch (error) {
    console.error("Error updating question:", error);
    alert("Failed to update the question. Please try again.");
  }
};

// Delete
export const deleteQuestion = async (
  id,
  setLoading,
  setError,
  setQuestions,
  setSelectedCardId
) => {
  try {
    const response = await axios.delete(
      `http://localhost:9200/api/question/delete/${id}`
    );

    setSelectedCardId(null);
    fetchQuestions(setLoading, setError, setQuestions);
  } catch (error) {
    console.error("Error deleting question:", error);
  }
};

// Create Results
export const createResult = async (
  time,
  testId,
  setResultID,
  candidateId,
  answersStorage,
  setError
) => {
  let resultData = {
    timeTaken: 0,
    testId: testId,
    candidateId: candidateId,
    answers: [
      {
        Answers: answersStorage,
      },
    ],
  };

  try {
    const result = await axios.post(
      `http://localhost:9200/api/results/create`,
      resultData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const resultId = result.data.id;
    setResultID(resultId);
  } catch (error) {
    console.error("Error creating result:", error);
    setError(true);
  }
};

// Update Result
export const updateResult = async (
  time,
  testId,
  resultID,
  candidateId,
  answersStorage
) => {
  let resultData = {
    timeTaken: time,
    testId: testId,
    candidateId: candidateId,
    answers: [
      {
        Answers: answersStorage,
      },
    ],
  };

  try {
    await axios.put(
      `http://localhost:9200/api/results/update/${resultID}`,
      resultData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  } catch (error) {
    console.error("Error updating result:", error);
    alert("Failed to update result. Please try again.");
  }
};

// Mark Result
export const markResult = async (
  time,
  testId,
  resultID,
  candidateId,
  answersStorage
) => {
  let resultData = {
    timeTaken: time,
    testId: testId,
    candidateId: candidateId,
    answers: [
      {
        Answers: answersStorage,
      },
    ],
  };

  try {
    await axios.put(
      `http://localhost:9200/api/results/update/${resultID}`,
      resultData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    await axios.put(`http://localhost:9200/api/results/mark/${resultID}`);
  } catch (error) {
    console.error("Error marking result:", error);
    alert("Failed to mark result. Please try again.");
  }
};
/*
 * Assessments module backend helpers
 * Ryan
 */

// Get all Assessments
export const fetchAssessments = async (
  setLoading,
  setError,
  setAssessments
) => {
  try {
    setLoading(true);
    setError(null);

    const res = await axios.get(`http://localhost:9200/api/test/all`);

    const updatedAssessments = res?.data.filter(
      (assessments) => !assessments.isDeleted
    );
    setAssessments(updatedAssessments);
  } catch (error) {
    setError(error.message);
  } finally {
    setLoading(false);
  }
};

// Get Assessments by id
export const getSingleAssessment = async (
  id,
  setLoading,
  setAssessmentData
) => {
  try {
    setLoading(true);
    const res = await axios.get(`http://localhost:9200/api/test/${id}`);
    const assessmentData = res?.data || null;
    setAssessmentData(assessmentData);
    return assessmentData;
  } catch (error) {
    console.error("Error fetching assessment: ", error);
  } finally {
    setLoading(false);
  }
};

// Delete Assessment
export const deleteAssessment = async (
  id,
  setLoading,
  setError,
  setAssessment,
  setSelectedCardId
) => {
  try {
    const res = await axios.delete(
      `http://localhost:9200/api/question/delete/${id}`
    );
    setSelectedCardId(null);
    fetchAssessments(setLoading, setError, setAssessment);
  } catch (error) {
    console.error("Error deleting question: ", error);
  }
};
// Create Assessments
export const createAssessment = async (values) => {
  try {
    const response = await axios.post(
      "http://localhost:9200/api/test/create",
      {
        assessmentName: values.assessmentName,
        testLevel: values.testLevel,
        expiryDate: values.expiryDate,
        startTime: values.startTime,
        durationHr: values.durationHr,
        durationMin: values.durationMin,
        scoreMin: values.scoreMin,
        testQuestions: values.testQuestions.map((q) => ({
          questionId: q.questionId,
          weightage: q.weightage,
        })),
      },
      {
        params: { randomize: values.randomize },
      }
    );
    if (response.status === 201) {
      alert("Created successfully");
      return response.data;
    } else {
      throw new Error(response.data.nessage || "Failed to create assessment");
    }
  } catch (error) {
    console.error("Error creating:", error);
    alert(error.response?.data?.message || "Failed to create assessment");
  }
};
